import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { notification } from '@spark/ui-components/utils/notification';
import { createPremiacaoTabelaVeiculoLote } from '@spark/services/premiacao';
import { usePremiacaoVeiculoProprioSelection } from '../hooks/usePremiacaoVeiculoProprioSelection';
import {
  getPremiacaoVeiculoProprioColumns,
  premiacaoVeiculoProprioFilterConfig
} from '../config/premiacaoVeiculoProprioColumns';

const PremiacaoVeiculoProprioSelectionModal = ({
  show,
  onHide,
  idTabelaPremiacao,
  onSuccess
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const {
    veiculos,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = usePremiacaoVeiculoProprioSelection(show, idTabelaPremiacao);

  useEffect(() => {
    if (!show) {
      setSelectedRows([]);
      setAdding(false);
    }
  }, [show]);

  const pageSelectableVeiculos = useMemo(
    () => veiculos.filter((row) => !row.jaVinculadaTabelaAtual),
    [veiculos]
  );

  const selectedIds = useMemo(
    () => selectedRows.map((row) => row.id),
    [selectedRows]
  );

  const allPageSelected = useMemo(
    () => pageSelectableVeiculos.length > 0
      && pageSelectableVeiculos.every((row) => selectedIds.includes(row.id)),
    [pageSelectableVeiculos, selectedIds]
  );

  const selectAllDisabled = pageSelectableVeiculos.length === 0;

  const handleToggleSelect = useCallback((rowId) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.id === rowId);
      if (exists) {
        return prev.filter((row) => row.id !== rowId);
      }

      const row = veiculos.find((item) => item.id === rowId);
      if (!row || row.jaVinculadaTabelaAtual) {
        return prev;
      }

      return [...prev, row];
    });
  }, [veiculos]);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedRows((prev) => {
      if (allPageSelected) {
        const pageIds = new Set(pageSelectableVeiculos.map((row) => row.id));
        return prev.filter((row) => !pageIds.has(row.id));
      }

      const merged = new Map(prev.map((row) => [row.id, row]));
      pageSelectableVeiculos.forEach((row) => {
        merged.set(row.id, row);
      });
      return Array.from(merged.values());
    });
  }, [allPageSelected, pageSelectableVeiculos]);

  const handleAdd = useCallback(async () => {
    if (!idTabelaPremiacao) {
      notification.warning('Salve ou selecione uma tabela de premiacao antes de vincular veiculos.');
      return;
    }

    if (selectedRows.length === 0 || adding) {
      return;
    }

    try {
      setAdding(true);
      const numerosFrota = selectedRows.map((row) => row.numeroFrota).filter(Boolean);
      const result = await createPremiacaoTabelaVeiculoLote({
        IdTabelaPremiacao: idTabelaPremiacao,
        NumerosFrota: numerosFrota
      });
      const data = result?.data || result || {};
      const inseridos = data.inseridos ?? data.Inseridos ?? 0;
      const atualizados = data.atualizados ?? data.Atualizados ?? 0;
      const total = inseridos + atualizados;

      notification.success(
        total > 0
          ? `${total} frota(s) vinculada(s) com sucesso!`
          : 'Nenhuma frota nova foi vinculada.'
      );

      if (onSuccess) {
        onSuccess();
      }

      setSelectedRows([]);
      reload();
      onHide();
    } catch (error) {
      const mensagem = error?.response?.data?.error || error?.message || 'Erro ao vincular frotas';
      notification.error(mensagem, 'Erro ao vincular frotas');
      console.error('Erro ao vincular frotas em lote:', error);
    } finally {
      setAdding(false);
    }
  }, [adding, idTabelaPremiacao, onHide, onSuccess, reload, selectedRows]);

  const columns = useMemo(
    () => getPremiacaoVeiculoProprioColumns({
      selectedIds,
      onToggleSelect: handleToggleSelect,
      onToggleSelectAll: handleToggleSelectAll,
      allPageSelected,
      selectAllDisabled
    }),
    [
      selectedIds,
      handleToggleSelect,
      handleToggleSelectAll,
      allPageSelected,
      selectAllDisabled
    ]
  );

  return (
    <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
      <Modal.Header closeButton>
        <Modal.Title>Selecionar Veículos Próprios</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={premiacaoVeiculoProprioFilterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Busca"
          loading={loading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`premiacao-veiculo-proprio-${paginaAtual}-${quantidadePorPagina}`}
          columns={columns}
          data={veiculos}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[10, 25, 50, 100]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 25
          }}
          onPaginationChange={handlePaginationChange}
          enableCopy={true}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedRows.length > 0 ? (
              <span>{selectedRows.length} frota(s) selecionada(s)</span>
            ) : (
              <span>
                {totalRegistros > 0
                  ? `Exibindo ${veiculos.length} de ${totalRegistros} veiculo(s) — ${pageSelectableVeiculos.length} elegivel(is) nesta pagina`
                  : 'Nenhum veiculo encontrado'}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={onHide} disabled={adding}>
              Cancelar
            </Button>
            <ButtonWithLoading
              variant="primary"
              type="button"
              onClick={handleAdd}
              loading={adding}
              disabled={selectedRows.length === 0}
            >
              Adicionar
            </ButtonWithLoading>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PremiacaoVeiculoProprioSelectionModal;
